import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowLeft,
  Calendar,
  Cat,
  Heart,
  Info,
  PawPrint,
  VenusAndMars,
  X,
  ShieldCheck,
  Home,
  Baby,
} from "lucide-react";
import CatNotFound from "../components/layout/CatNotFound";

export default function CatDetailPage() {
  const { slug } = useParams();
  const [cat, setCat] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    fetch("/data/adoptable-cats.json")
      .then((res) => res.json())
      .then((data) => {
        setCat(data.find((item) => item.slug === slug) || null);
      })
      .catch(() => setCat(null));
  }, [slug]);

  // if (!cat) {
  //   return (
  //     <main className="min-h-screen bg-[#f8f5f0] px-6 py-24 text-center">
  //       <p className="font-semibold">Chat introuvable.</p>
  //       <Link to="/adoption" className="mt-4 inline-flex text-red-600">
  //         Retour aux chats
  //       </Link>
  //     </main>
  //   );
  // }

  if (!cat) {
    return <CatNotFound />;
  }

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#f8f5f0] text-[#111111]">
      <Helmet>
        <title>{cat.name} à adopter | Savoinimaux</title>
        <meta
          name="description"
          content={`${cat.name}, ${cat.sex || "chat"}, ${cat.ageLabel}, est proposé(e) à l’adoption par Savoinimaux.`}
        />
        <link
          rel="canonical"
          href={`https://savoinimaux.com/adoption/${cat.slug}`}
        />
      </Helmet>

      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[linear-gradient(180deg,#fcfaf7_0%,#f8f4ee_45%,#f1ebe3_100%)]" />
        <div className="absolute -left-52 top-10 h-[520px] w-[520px] rounded-full bg-red-100/70 blur-3xl" />
        <div className="absolute right-[-180px] top-52 h-[600px] w-[600px] rounded-full bg-[#ead7ca]/70 blur-3xl" />
      </div>

      <section className="relative mx-auto grid max-w-7xl gap-10 px-6 py-20 lg:grid-cols-[0.9fr_1.1fr] lg:px-10">
        <div>
          <Link
            to="/adoption"
            className="mb-8 inline-flex items-center gap-2 rounded-full bg-white/90 px-5 py-3 text-sm font-semibold text-black/65 shadow-sm backdrop-blur-xl transition hover:text-red-600"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour aux chats
          </Link>

          <div className="relative overflow-hidden rounded-[2.4rem] border border-white/70 bg-white/80 p-4 shadow-[0_24px_90px_rgba(0,0,0,0.07)] backdrop-blur-xl">
            <img
              src={cat.image}
              alt={cat.name}
              className="w-full rounded-[1.9rem] object-cover"
              loading="eager"
            />

            <div className="absolute inset-4 rounded-[1.9rem] bg-gradient-to-t from-black/45 via-black/5 to-transparent" />

            {cat.urgent && (
              <span className="absolute left-8 top-8 rounded-full bg-red-600 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-white shadow-[0_12px_34px_rgba(220,38,38,0.28)]">
                Urgent
              </span>
            )}
          </div>

          <div className="mt-6 rounded-[2rem] border border-white/70 bg-white/80 p-6 shadow-[0_18px_60px_rgba(0,0,0,0.04)] backdrop-blur-xl">
            <h2 className="text-3xl font-semibold">{cat.race}</h2>

            <div className="mt-5 space-y-3 text-sm text-black/62">
              <p className="flex items-center gap-3">
                <VenusAndMars className="h-5 w-5 text-red-600" />
                <strong className="text-black">Sexe :</strong> {cat.sex}
              </p>

              <p className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-red-600" />
                <strong className="text-black">Âge :</strong> {cat.ageLabel}
              </p>

              {cat.birthDate && (
                <p className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-red-600" />
                  <strong className="text-black">Naissance :</strong>{" "}
                  {cat.birthDate}
                </p>
              )}

              <p className="flex items-center gap-3">
                <Heart className="h-5 w-5 text-red-600" />
                <strong className="text-black">Stérilisation :</strong>{" "}
                {cat.sterilized === null
                  ? "Information à confirmer"
                  : cat.sterilized
                  ? "Stérilisé(e)"
                  : "Non stérilisé(e)"}
              </p>

              <p className="flex items-center gap-3">
                <Info className="h-5 w-5 text-red-600" />
                <strong className="text-black">FIV :</strong>{" "}
                {cat.fiv ? "Positif" : "Négatif"}
              </p>

              {cat.indoorOnly && (
                <p className="flex items-center gap-3">
                  <Home className="h-5 w-5 text-red-600" />
                  Vie en intérieur recommandée
                </p>
              )}

              {cat.withOtherAnimals === false && (
                <p className="flex items-center gap-3">
                  <PawPrint className="h-5 w-5 text-red-600" />
                  Placement sans autres animaux conseillé
                </p>
              )}

              {cat.withChildren === false && (
                <p className="flex items-center gap-3">
                  <Baby className="h-5 w-5 text-red-600" />
                  Foyer sans jeunes enfants conseillé
                </p>
              )}
            </div>

            <button
              onClick={() => setModalOpen(true)}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#d99b43] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#c88b38]"
            >
              <Info className="h-4 w-4" />
              Modalités et frais d’adoption
            </button>
          </div>
        </div>

        <div className="flex flex-col justify-center">
          <p className="text-xs uppercase tracking-[0.35em] text-black/38">
            Présentation
          </p>

          <h1 className="mt-4 text-5xl font-semibold leading-[0.95] tracking-[-0.04em] md:text-7xl">
            {cat.name}
          </h1>

          <p className="mt-4 text-xl font-medium text-black/55">
            {cat.sex} · {cat.ageLabel}
          </p>

          <div className="mt-10 rounded-[2.2rem] border border-white/70 bg-white/80 p-8 shadow-[0_24px_80px_rgba(0,0,0,0.04)] backdrop-blur-xl">
            <Cat className="h-8 w-8 text-red-600" />
            <p className="mt-6 whitespace-pre-line text-lg leading-9 text-black/65">
              {cat.presentation}
            </p>
          </div>

          {cat.personality?.length > 0 && (
            <div className="mt-8 rounded-[2rem] border border-white/70 bg-white/80 p-7 shadow-[0_18px_60px_rgba(0,0,0,0.04)] backdrop-blur-xl">
              <h2 className="text-2xl font-semibold">Son caractère</h2>

              <div className="mt-5 flex flex-wrap gap-3">
                {cat.personality.map((trait) => (
                  <span
                    key={trait}
                    className="rounded-full bg-red-50 px-4 py-2 text-sm font-semibold text-red-600"
                  >
                    {trait}
                  </span>
                ))}
              </div>
            </div>
          )}

          {cat.idealHome && (
            <div className="mt-8 rounded-[2rem] border border-white/70 bg-white/80 p-7 shadow-[0_18px_60px_rgba(0,0,0,0.04)] backdrop-blur-xl">
              <h2 className="text-2xl font-semibold">Famille idéale</h2>
              <p className="mt-4 text-base leading-8 text-black/62">
                {cat.idealHome}
              </p>
            </div>
          )}

          <div className="mt-8 flex flex-col gap-4 sm:flex-row">
            <a
              href="/#contact-form"
              className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-red-600 to-red-500 px-7 py-4 text-sm font-semibold text-white shadow-[0_18px_55px_rgba(220,38,38,0.22)] transition hover:-translate-y-0.5"
            >
              Prendre contact avec Savoinimaux
            </a>

            <button
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center justify-center rounded-full border border-black/10 bg-white px-7 py-4 text-sm font-semibold text-[#111111] transition hover:bg-red-50"
            >
              Voir les modalités
            </button>
          </div>
        </div>
      </section>

      {modalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
          <button
            className="absolute inset-0 bg-black/45 backdrop-blur-sm"
            onClick={() => setModalOpen(false)}
            aria-label="Fermer"
          />

          <div className="relative z-10 max-h-[88vh] w-full max-w-3xl overflow-y-auto rounded-[2rem] bg-white p-7 shadow-[0_30px_100px_rgba(0,0,0,0.25)]">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.35em] text-black/38">
                  Adoption
                </p>
                <h2 className="mt-3 text-3xl font-semibold">
                  Modalités et frais
                </h2>
              </div>

              <button
                onClick={() => setModalOpen(false)}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-black/10 bg-white text-[#111111] transition hover:bg-red-50"
                aria-label="Fermer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-6 space-y-5 text-sm leading-7 text-black/65">
              <p className="rounded-[1.4rem] bg-red-50 p-5 font-semibold text-black">
                Nous ne proposons actuellement que des chats à l’adoption.
              </p>

              <div>
                <p className="font-semibold text-black">
                  1. Premier contact & visite à domicile
                </p>
                <p className="mt-2">
                  Un(e) bénévole échange avec vous sur votre projet d’adoption,
                  répond à vos questions et vérifie que l’environnement
                  correspond à l’animal.
                </p>
              </div>

              <div>
                <p className="font-semibold text-black">2. Contrat d’adoption</p>
                <p className="mt-2">
                  Un contrat est établi avec engagement contre la maltraitance,
                  interdiction de reproduction et restitution de l’animal en cas
                  de problème.
                </p>
              </div>

              <div className="rounded-[1.4rem] bg-[#faf7f2] p-5">
                <p className="font-semibold text-black">
                  Frais d’adoption 2025-2026
                </p>
                <p className="mt-2">Chat mâle : 220 €</p>
                <p>Chat femelle : 260 €</p>
              </div>

              <p>
                Les frais incluent : identification, primo-vaccination TC, test
                FIV/FELV, vermifuge, déparasitage, stérilisation/castration,
                check-up complet et certificat de bonne santé.
              </p>

              <a
                href="/documents/Modalites_Adoption_Savoinimaux_2026.pdf"
                target="_blank"
                rel="noreferrer"
                className="inline-flex rounded-full bg-red-600 px-6 py-3 font-semibold text-white"
              >
                Télécharger les modalités complètes
              </a>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

